'use client';

/**
 * HudPanel — the canonical cockpit-chrome primitive for OPMODE.
 *
 * Every surface that wants the avionics aesthetic (classification banner,
 * corner brackets, severity-coded side bar, mono badge header) should use
 * this instead of hand-rolling the chrome.
 *
 * Variants
 *   hero      — full chrome: classification banner, all 4 corner brackets,
 *               top accent line, severity side bar. Use for the dominant
 *               panel on a page (Operator Score ring, today's order,
 *               reentry plan).
 *   standard  — moderate chrome: 2 bottom corner brackets, top accent line,
 *               severity side bar, badge header. Use for stacked sections.
 *   utility   — minimal chrome: just severity side bar + badge header. Use
 *               for low-priority lists (legal, account, history).
 *
 * Severities map to color tokens consistent across the app:
 *   gold     — primary action / gated upgrade
 *   emerald  — active state / accepted plan / on-track
 *   cyan     — informational / data signal
 *   red      — critical / danger zone
 *   slate    — neutral utility
 */

import type { ReactNode } from 'react';

export type HudVariant = 'hero' | 'standard' | 'utility';
export type HudSeverity = 'gold' | 'emerald' | 'cyan' | 'red' | 'slate';

interface Props {
  /** Visual weight. Default: 'standard'. */
  variant?: HudVariant;
  /** Side-bar / accent color. Default: 'gold'. */
  severity?: HudSeverity;
  /**
   * Classification line above the panel — e.g. "OPS // RECONDITIONING".
   * Renders only on `variant="hero"`. Live UTC clock if `showClock`.
   */
  classification?: string;
  /** Render a live UTC clock in the classification banner's right slot. */
  showClock?: boolean;
  /** Top-left badge label (ALL CAPS mono). */
  badge?: string;
  /** Top-right system code or short stat (e.g. "SYS·WRK·01" or "3/5"). */
  code?: ReactNode;
  /** Right-aligned stat callout in the header (overrides `code`). */
  callout?: ReactNode;
  /** Children — the panel body. */
  children: ReactNode;
  /** Tighter vertical padding. */
  dense?: boolean;
  /** Tailwind classes appended to the outer section. */
  className?: string;
  /** Wrap a clickable link / button — applies `block` + active scale. */
  as?: 'section' | 'div';
}

const SEVERITY_TOKENS: Record<HudSeverity, {
  bar: string;
  borderMain: string;
  borderBrkt: string;
  badgeTw: string;
  accentLine: string;
  glow: string;
}> = {
  gold: {
    bar: 'bg-amber-500',
    borderMain: 'border-amber-500/30',
    borderBrkt: 'border-amber-500/60',
    badgeTw: 'text-amber-400',
    accentLine: 'via-amber-500/70',
    glow: 'shadow-[0_0_40px_-16px_rgba(245,158,11,0.25)]',
  },
  emerald: {
    bar: 'bg-emerald-500',
    borderMain: 'border-emerald-500/35',
    borderBrkt: 'border-emerald-500/60',
    badgeTw: 'text-emerald-400',
    accentLine: 'via-emerald-500/70',
    glow: 'shadow-[0_0_40px_-16px_rgba(74,222,128,0.25)]',
  },
  cyan: {
    bar: 'bg-hud-cyan',
    borderMain: 'border-hud-cyan/30',
    borderBrkt: 'border-hud-cyan/60',
    badgeTw: 'text-hud-cyan',
    accentLine: 'via-hud-cyan/70',
    glow: 'shadow-[0_0_40px_-16px_rgba(103,232,249,0.25)]',
  },
  red: {
    bar: 'bg-red-500',
    borderMain: 'border-red-500/35',
    borderBrkt: 'border-red-500/60',
    badgeTw: 'text-red-400',
    accentLine: 'via-red-500/70',
    glow: 'shadow-[0_0_40px_-16px_rgba(239,68,68,0.25)]',
  },
  slate: {
    bar: 'bg-slate-600',
    borderMain: 'border-slate-700/60',
    borderBrkt: 'border-slate-700',
    badgeTw: 'text-slate-300',
    accentLine: 'via-slate-600/60',
    glow: '',
  },
};

export default function HudPanel({
  variant = 'standard',
  severity = 'gold',
  classification,
  showClock = false,
  badge,
  code,
  callout,
  children,
  dense = false,
  className = '',
  as = 'section',
}: Props) {
  const t = SEVERITY_TOKENS[severity];
  const Tag = (as === 'div' ? 'div' : 'section') as 'div' | 'section';

  const isHero = variant === 'hero';
  const isUtility = variant === 'utility';

  const padding = dense
    ? 'px-3 pt-2 pb-2.5'
    : isHero
      ? 'px-4 pt-3 pb-4'
      : 'px-4 pt-2.5 pb-3';

  return (
    <Tag
      className={`relative overflow-hidden rounded-xl border bg-hud-ink/85 ${t.borderMain} ${isHero ? t.glow : ''} ${className}`}
    >
      {/* Severity side bar — always present except utility (no bar, just border) */}
      {!isUtility && (
        <span aria-hidden className={`absolute top-0 bottom-0 left-0 w-1 ${t.bar}`} />
      )}

      {/* Top accent line — hero + standard only */}
      {!isUtility && (
        <div
          aria-hidden
          className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${t.accentLine} to-transparent`}
        />
      )}

      {/* Corner brackets — hero gets all 4, standard gets bottom 2, utility gets none */}
      {isHero && (
        <>
          <span aria-hidden className={`pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l ${t.borderBrkt}`} />
          <span aria-hidden className={`pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r ${t.borderBrkt}`} />
          <span aria-hidden className={`pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l ${t.borderBrkt}`} />
          <span aria-hidden className={`pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r ${t.borderBrkt}`} />
        </>
      )}
      {variant === 'standard' && (
        <>
          <span aria-hidden className={`pointer-events-none absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l ${t.borderBrkt}`} />
          <span aria-hidden className={`pointer-events-none absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r ${t.borderBrkt}`} />
        </>
      )}

      {/* Classification banner — hero only when set */}
      {isHero && (classification || showClock) && (
        <header className={`relative flex items-center justify-between px-4 py-1.5 border-b ${t.borderMain} font-mono text-[9px] uppercase tracking-[0.28em]`}>
          <div className="flex items-center gap-2">
            <span className={`w-1 h-1 rounded-full ${t.bar} animate-hud-blink`} aria-hidden />
            {classification && <span className={t.badgeTw}>{classification}</span>}
          </div>
          {showClock && <UtcClock className="text-slate-500" />}
        </header>
      )}

      <div className={padding + ' pl-4'}>
        {/* Badge header */}
        {(badge || code || callout) && (
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="flex items-center gap-2 min-w-0">
              {!isUtility && <span className={`w-0.5 h-3 ${t.bar}`} aria-hidden />}
              {badge && (
                <span className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold truncate ${t.badgeTw}`}>
                  {badge}
                </span>
              )}
              {code && !callout && (
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 hidden sm:inline truncate">
                  {code}
                </span>
              )}
            </div>
            {callout && (
              <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-300 tabular-nums shrink-0">
                {callout}
              </div>
            )}
          </div>
        )}

        {children}
      </div>
    </Tag>
  );
}

// ============================================================================
// UTC clock helper — small, internal, used by hero variant classification.
// ============================================================================
function UtcClock({ className = '' }: { className?: string }) {
  // Server-rendered HTML can't show the live clock, so we render a placeholder
  // on first paint and let the client effect populate. The mismatch is
  // suppressed via `suppressHydrationWarning` on a span (acceptable for a
  // continuously-updating value).
  return (
    <span suppressHydrationWarning className={className}>
      <UtcClockInner />
    </span>
  );
}

import { useEffect, useState } from 'react';

function UtcClockInner() {
  const [now, setNow] = useState<string>('—');
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      setNow(`${hh}${mm}Z`);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);
  return <>{now}</>;
}
